import type { HttpRetryConfig } from '../../HttpClient.types';
import { DEFAULT_RETRY_CONFIG } from '../../HttpClient.types';

/**
 * Error type that includes HTTP status code for retry logic.
 */
export interface RetryableError {
  message?: string;
  statusCode?: number;
}

/**
 * Interface for the retry-with-backoff utility.
 */
export interface IRetryWithBackoff {
  calculateBackoffDelay(attempt: number, config: HttpRetryConfig): number;
  execute<T>(
    fn: () => Promise<T>,
    config?: Partial<HttpRetryConfig>,
    onRetry?: (attempt: number, delay: number, error: RetryableError) => void
  ): Promise<T>;
  isRetryableError(error: RetryableError, config: HttpRetryConfig): boolean;
}

export const retryWithBackoff: IRetryWithBackoff = {
  /**
   * Calculates the delay for exponential backoff with jitter.
   * @param attempt - The current attempt number (0-indexed).
   * @param config - The retry configuration.
   * @returns The delay in milliseconds.
   */
  calculateBackoffDelay(attempt: number, config: HttpRetryConfig): number {
    const exponentialDelay = config.baseDelayMs * Math.pow(2, attempt);
    const jitter = Math.random() * 0.3 * exponentialDelay;
    const delayWithJitter = exponentialDelay + jitter;
    return Math.min(delayWithJitter, config.maxDelayMs);
  },
  /**
   * Executes a function with exponential backoff retry logic.
   * @param fn - The async function to execute.
   * @param config - Optional retry configuration (uses defaults if not provided).
   * @param onRetry - Optional callback called before each retry with attempt number and delay.
   * @returns The result of the function.
   * @throws The last error if all retries are exhausted.
   */
  async execute<T>(
    fn: () => Promise<T>,
    config: Partial<HttpRetryConfig> = {},
    onRetry?: (attempt: number, delay: number, error: RetryableError) => void
  ): Promise<T> {
    const fullConfig: HttpRetryConfig = { ...DEFAULT_RETRY_CONFIG, ...config };
    let lastError: RetryableError | undefined;
    for (let attempt = 0; attempt <= fullConfig.maxRetries; attempt++) {
      try {
        return await fn();
      } catch (err: unknown) {
        const retryableError: RetryableError =
          typeof err === 'object' && err !== null
            ? { message: (err as RetryableError).message, statusCode: (err as RetryableError).statusCode }
            : { message: String(err) };
        lastError = retryableError;
        const isLastAttempt = attempt === fullConfig.maxRetries;
        const shouldRetry = !isLastAttempt && this.isRetryableError(retryableError, fullConfig);
        if (!shouldRetry) throw err;
        const delay = this.calculateBackoffDelay(attempt, fullConfig);
        if (onRetry) onRetry(attempt + 1, delay, retryableError);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    throw lastError;
  },
  /**
   * Determines whether an error should trigger a retry based on status code.
   * @param error - The error to check.
   * @param config - The retry configuration.
   * @returns True if the error is retryable.
   */
  isRetryableError(error: RetryableError, config: HttpRetryConfig): boolean {
    if (error.statusCode === undefined) return false;
    return config.retryableStatusCodes.includes(error.statusCode);
  }
};
